import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DatabaseZap,
  FileSpreadsheet,
  RefreshCw,
  Play,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  Ban,
} from "lucide-react";

const DATASETS = [
  { key: "tasks", label: "Jobs", file: "Tasks.csv", expected: "~8,530" },
  { key: "contacts", label: "Users / Contacts", file: "Users.csv", expected: "~19,953" },
  { key: "products", label: "Products", file: "Stock.csv", expected: "~2,355" },
  { key: "assets", label: "Vehicles / Assets", file: "Assets.csv", expected: "6" },
];

function StatusPill({ tone, children }) {
  const tones = {
    inserted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    updated: "bg-blue-50 text-blue-700 border-blue-200",
    duplicate: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    issue: "bg-purple-50 text-purple-700 border-purple-200",
    neutral: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

const METRICS = [
  { key: "source_file", label: "Source File", testId: "metric-card-source-file", tone: "neutral" },
  { key: "dataset", label: "Dataset Type", testId: "metric-card-dataset-type", tone: "neutral" },
  { key: "rows_read", label: "Rows Read", testId: "metric-card-rows-read", tone: "neutral" },
  { key: "rows_inserted", label: "Rows Inserted", testId: "metric-card-rows-inserted", tone: "inserted" },
  { key: "rows_updated", label: "Rows Updated", testId: "metric-card-rows-updated", tone: "updated" },
  { key: "duplicates", label: "Duplicates", testId: "metric-card-duplicates", tone: "duplicate" },
  { key: "rejected_count", label: "Rejected Rows", testId: "metric-card-rejected-rows", tone: "rejected" },
  { key: "validation_issue_count", label: "Validation Issues", testId: "metric-card-validation-issues", tone: "issue" },
  { key: "final_record_count", label: "Final Record Count", testId: "metric-card-final-record-count", tone: "neutral" },
];

function fmt(v) {
  return typeof v === "number" ? v.toLocaleString() : v ?? "—";
}

export default function ImportValidation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(null); // dataset key or "all"
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/imports/summary");
    setSummary(data);
  }, []);

  const loadBatches = useCallback(
    async (p) => {
      const { data } = await api.get("/imports/batches", {
        params: { page: p, page_size: 10 },
      });
      setBatches(data);
      setSelected((prev) => prev || data.items[0] || null);
      return data;
    },
    []
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSummary(), loadBatches(1)]).finally(() => setLoading(false));
  }, [loadSummary, loadBatches]);

  const runImport = async (dataset) => {
    setRunning(dataset);
    try {
      if (dataset === "all") {
        const { data } = await api.post("/imports/run-all");
        const ok = data.results.filter((r) => !r.error);
        toast.success(`Imported ${ok.length} dataset(s)`);
        setSelected(ok[ok.length - 1] || null);
      } else {
        const { data } = await api.post("/imports/run", { dataset });
        toast.success(
          `${data.source_file}: ${fmt(data.rows_inserted)} inserted, ${fmt(
            data.rows_updated
          )} updated`
        );
        setSelected(data);
      }
      await Promise.all([loadSummary(), loadBatches(1)]);
      setPage(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setRunning(null);
    }
  };

  const gotoPage = async (p) => {
    if (p < 1 || p > batches.total_pages) return;
    setPage(p);
    await loadBatches(p);
  };

  const totalRecords = useMemo(() => {
    if (!summary) return 0;
    return Object.values(summary).reduce((a, s) => a + (s.record_count || 0), 0);
  }, [summary]);

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading import state…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="page-import-validation">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <DatabaseZap className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Import Validation
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Idempotent legacy CSV importers · {fmt(totalRecords)} operational records loaded
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button
            onClick={() => runImport("all")}
            disabled={!!running}
            data-testid="run-all-imports-button"
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
          >
            {running === "all" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Re-import all datasets
          </Button>
        )}
      </div>

      {/* Dataset cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {DATASETS.map((d) => {
          const info = summary?.[d.key];
          const busy = running === d.key || running === "all";
          return (
            <div
              key={d.key}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col hover:border-indigo-200 transition-colors"
              data-testid={`dataset-card-${d.key}`}
            >
              <div className="flex items-center gap-2 text-slate-400 mb-3">
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-xs font-medium truncate">{d.file}</span>
              </div>
              <div className="text-sm font-semibold text-slate-900">{d.label}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">
                  {fmt(info?.record_count ?? 0)}
                </span>
                <span className="text-xs text-slate-400">/ {d.expected}</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">records in database</div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runImport(d.key)}
                  disabled={!!running}
                  data-testid={`run-import-${d.key}`}
                  className="mt-4 gap-1.5"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Play className="w-3.5 h-3.5" />
                  )}
                  Import
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected batch metrics */}
      <h2 className="text-lg font-semibold text-slate-900 mb-3">
        {selected ? "Latest import result" : "No imports yet"}
      </h2>
      {selected ? (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3 mb-4"
          data-testid="selected-batch-metrics"
        >
          {METRICS.map((m) => (
            <div
              key={m.key}
              data-testid={m.testId}
              className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide truncate">
                {m.label}
              </div>
              <div className="text-base font-bold text-slate-900 mt-1 truncate" title={String(selected[m.key])}>
                {m.key === "dataset" ? (
                  <span className="capitalize">{selected[m.key]}</span>
                ) : (
                  fmt(selected[m.key])
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500 mb-4">
          {isAdmin
            ? "Run an importer above to see validation metrics."
            : "No imports have been run yet."}
        </div>
      )}

      {/* Validation issues + rejected detail */}
      {selected && (selected.validation_issues?.length > 0 || selected.rejected_rows?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4 text-purple-500" />
              <h3 className="text-sm font-semibold text-slate-900">
                Validation issues ({fmt(selected.validation_issue_count)})
              </h3>
            </div>
            {selected.validation_issues?.length ? (
              <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {selected.validation_issues.map((v, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="mono text-slate-400">#{v.legacy_id}</span>
                    <StatusPill tone="issue">{v.issue}</StatusPill>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-400">No validation issues.</p>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <Ban className="w-4 h-4 text-rose-500" />
              <h3 className="text-sm font-semibold text-slate-900">
                Rejected rows ({fmt(selected.rejected_count)})
              </h3>
            </div>
            {selected.rejected_rows?.length ? (
              <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {selected.rejected_rows.map((r, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                    <span className="mono text-slate-400">row {r.row}</span>
                    <StatusPill tone="rejected">{r.reason}</StatusPill>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-400">No rejected rows.</p>
            )}
          </div>
        </div>
      )}

      {/* Import history table */}
      <h2 className="text-lg font-semibold text-slate-900 mb-3">Import history</h2>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="import-batch-table">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Source File</th>
                <th className="px-4 py-3">Dataset</th>
                <th className="px-4 py-3 text-right">Read</th>
                <th className="px-4 py-3 text-right">Inserted</th>
                <th className="px-4 py-3 text-right">Updated</th>
                <th className="px-4 py-3 text-right">Duplicates</th>
                <th className="px-4 py-3 text-right">Rejected</th>
                <th className="px-4 py-3 text-right">Issues</th>
                <th className="px-4 py-3 text-right">Final Count</th>
                <th className="px-4 py-3">Imported</th>
              </tr>
            </thead>
            <tbody>
              {batches.items.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No imports recorded yet.
                  </td>
                </tr>
              )}
              {batches.items.map((b) => (
                <tr
                  key={b.batch_id}
                  onClick={() => setSelected(b)}
                  className={`border-t border-slate-100 text-sm cursor-pointer transition-colors hover:bg-slate-50 ${
                    selected?.batch_id === b.batch_id ? "bg-indigo-50/50" : ""
                  }`}
                  data-testid={`batch-row-${b.batch_id}`}
                >
                  <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                    {b.source_file}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill tone="neutral">
                      <span className="capitalize">{b.dataset}</span>
                    </StatusPill>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{fmt(b.rows_read)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-medium">
                    {fmt(b.rows_inserted)}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-700 font-medium">
                    {fmt(b.rows_updated)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber-700">{fmt(b.duplicates)}</td>
                  <td className="px-4 py-3 text-right text-rose-700">{fmt(b.rejected_count)}</td>
                  <td className="px-4 py-3 text-right text-purple-700">
                    {fmt(b.validation_issue_count)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">
                    {fmt(b.final_record_count)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {b.imported_date ? new Date(b.imported_date).toLocaleString("en-GB") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {batches.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
            <span className="text-slate-500">
              Page {batches.page} of {batches.total_pages} · {fmt(batches.total)} imports
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => gotoPage(page - 1)}
                disabled={page <= 1}
                data-testid="table-pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => gotoPage(page + 1)}
                disabled={page >= batches.total_pages}
                data-testid="table-pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 max-w-3xl">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
        <p>
          Re-importing the same CSV updates matching records by legacy ID without creating
          duplicates. Source files are never exposed publicly; home addresses, dates of birth and
          payroll data are never loaded into the operational records.
        </p>
      </div>
    </div>
  );
}
